// src/components/admin/AdminPortal.js
import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import AdminSidebar from './AdminSidebar';
import AdminHeader from './AdminHeader';
import AdminDashboard from './AdminDashboard';
import EmployeeManagement from './EmployeeManagement';
import ComprehensiveAttendanceManagement from './attendance/ComprehensiveAttendanceManagement';
import LeaveManagement from './LeaveManagement';
import PayrollManagement from './PayrollManagement';
import PayrollHub from './payroll/PayrollHub';
import PayrollRun2 from './payroll/PayrollRun2';
import PayrollRunEnhanced from './payroll/PayrollRunEnhanced';
import PerformanceManagement from './PerformanceManagement';
import OnboardingManagement from './onboarding/OnboardingManagementV2';
import OrgView from './onboarding/OrgView';
import ApprovalsManagement from './ApprovalsManagement';
import Reports from './Reports';
import ShiftManagement from './ShiftManagement';import CompanySettings from './CompanySettings';
import AssetManagement from './AssetManagement';
import ReportBuilder from './DocumentBuilderAPI';

import { TaskBox } from '../common/taskbox';
import { VibeFeed } from '../common/vibe';
import TeamTaskbox from './teamTaskbox/TeamTaskbox';
import SettingsContent from '../common/SettingsContent';
import DocumentBuilderEnhanced from './DocumentBuilderEnhanced';
import DocumentBuilder from './DocumentBuilder';
import PayslipGenerationProgress from './payroll/components/PayslipGenerationProgress';
import payrollService from '../../services/payroll.service';
import AIAvatarContainer from '../common/aia/AIAvatarContainer';
// BillingPlans (multi-tier UI) preserved on disk for future use — re-import to swap back
import BillingPlansSimple from './BillingPlansSimple';
import HelpCenter from './HelpCenter';
import FlowAutomation from './flows/FlowAutomation';
import ContactUs from './help/ContactUs';
import PrivacyPolicy from './help/PrivacyPolicy';
import TermsAndConditions from './help/TermsAndConditions';

import { useAuth } from '../../hooks/useAuth';

// Create context for global payslip generation state
export const PayslipJobContext = createContext(null);

// Hook to use the payslip job context
export const usePayslipJob = () => {
    const context = useContext(PayslipJobContext);
    if (!context) {
        throw new Error('usePayslipJob must be used within AdminPortal');
    }
    return context;
};

const AdminPortal = () => {
    const { user, logout } = useAuth();
    const location = useLocation();
    const [sidebarOpen, setSidebarOpen] = useState(true);

    // Global payslip generation state - persists across tab navigation
    const [payslipJobId, setPayslipJobId] = useState(null);
    const [showPayslipProgress, setShowPayslipProgress] = useState(false);
    const [payslipJobPeriod, setPayslipJobPeriod] = useState(null); // Track which period is being processed
    const [payslipJobCompleteCallback, setPayslipJobCompleteCallback] = useState(null);

    // Debug: Log location changes
    useEffect(() => {
        console.log('📍 Location changed to:', location.pathname);
    }, [location.pathname]);

    // Check for active payslip jobs on mount (resume after refresh)
    useEffect(() => {
        const checkActiveJobs = async () => {
            try {
                console.log('🔍 Checking for active payslip jobs...');
                const result = await payrollService.getActivePayslipJobs();

                if (result.success && result.jobs && result.jobs.length > 0) {
                    // Find jobs that are still processing or pending
                    const activeJob = result.jobs.find(job =>
                        job.status === 'processing' || job.status === 'pending'
                    );

                    if (activeJob) {
                        console.log('🔄 Found active payslip job, resuming progress:', activeJob.id, activeJob.periodName);
                        setPayslipJobId(activeJob.id);
                        setPayslipJobPeriod(activeJob.periodName); // Note: camelCase from backend
                        setShowPayslipProgress(true);
                        // No callback since we don't have the original context after refresh
                        setPayslipJobCompleteCallback(null);
                    } else {
                        console.log('✅ No active payslip jobs found');
                    }
                }
            } catch (error) {
                console.warn('Could not check for active payslip jobs:', error.message);
            }
        };

        // Only check if not already showing progress
        if (!showPayslipProgress) {
            checkActiveJobs();
        }
    }, []); // Run once on mount

    // Start a payslip generation job (called from PayrollRun)
    const startPayslipJob = useCallback((jobId, period, onComplete) => {
        console.log('🚀 Starting payslip job in AdminPortal:', jobId, period);
        setPayslipJobId(jobId);
        setPayslipJobPeriod(period);
        setShowPayslipProgress(true);
        // Store callback as a function that returns the callback
        setPayslipJobCompleteCallback(() => onComplete);
    }, []);

    // Handle job completion
    const handlePayslipJobComplete = useCallback((job) => {
        console.log('✅ Payslip job completed in AdminPortal:', job);
        if (payslipJobCompleteCallback) {
            payslipJobCompleteCallback(job);
        }
    }, [payslipJobCompleteCallback]);

    // Handle job cancellation
    const handlePayslipJobCancel = useCallback(() => {
        console.log('❌ Payslip job cancelled in AdminPortal');
        setShowPayslipProgress(false);
        setPayslipJobId(null);
        setPayslipJobPeriod(null);
        setPayslipJobCompleteCallback(null);
    }, []);

    // Handle closing the progress modal
    const handlePayslipProgressClose = useCallback(() => {
        setShowPayslipProgress(false);
        setPayslipJobId(null);
        setPayslipJobPeriod(null);
        setPayslipJobCompleteCallback(null);
    }, []);

    // Context value for child components
    const payslipJobContextValue = {
        jobId: payslipJobId,
        isRunning: showPayslipProgress && payslipJobId,
        period: payslipJobPeriod,
        startJob: startPayslipJob,
        cancelJob: handlePayslipJobCancel
    };

    if (!user?.is_admin && user?.role_name !== 'HR Admin' && user?.role_name !== 'Super Admin' && user?.access_level !== 'admin') {
        return <Navigate to="/employee" />;
    }

    return (
        <PayslipJobContext.Provider value={payslipJobContextValue}>
            <div className="h-screen flex overflow-hidden bg-gray-100 dark:bg-gray-900">
                <AdminSidebar
                    isOpen={sidebarOpen}
                    onToggle={() => setSidebarOpen(!sidebarOpen)}
                    user={user}
                />

                <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
                    <AdminHeader user={user} onLogout={logout} />

                    <main className="flex-1 p-6 overflow-y-auto">
                        <Routes>
                            <Route path="/" element={<Navigate to="/admin/dashboard" />} />
                            <Route path="/dashboard" element={<AdminDashboard />} />
                            <Route path="/employees/*" element={<EmployeeManagement />} />
                            <Route path="/onboarding" element={<OnboardingManagement />} />
                            <Route path="/org-view/*" element={<OrgView />} />
                            <Route path="/attendance/*" element={<ComprehensiveAttendanceManagement />} />
                            <Route path="/approvals" element={<ApprovalsManagement />} />
                            <Route path="/leaves" element={<LeaveManagement />} />
                            <Route path="/payroll/*" element={<PayrollManagement />} />
                            <Route path="/payrollrun" element={<PayrollHub />} />
                            <Route path="/payrollrun2" element={<PayrollRun2 />} />
                            <Route path="/payrollrun-enhanced" element={<PayrollRunEnhanced />} />
                            <Route path="/performance/*" element={<PerformanceManagement />} />
                            <Route path="/reports/*" element={<Reports />} />
                            <Route path="/settings/*" element={<CompanySettings />} />
                            <Route path="/billing" element={<BillingPlansSimple />} />
                            <Route path="/help" element={<HelpCenter />} />
                            <Route path="/contact" element={<ContactUs />} />
                            <Route path="/privacy" element={<PrivacyPolicy />} />
                            <Route path="/terms" element={<TermsAndConditions />} />
                            <Route path="/my-settings" element={<SettingsContent />} />
                            <Route path="/shifts" element={<ShiftManagement />} />
                            <Route path="/reports-builder/*" element={<DocumentBuilderEnhanced />} />
                            <Route path="/document-builder" element={<DocumentBuilderEnhanced />} />
                            <Route path="/taskbox" element={<TaskBox userRole="admin" />} />
                            <Route path="/team-taskbox" element={<TeamTaskbox />} />
                            <Route path="/vibe" element={<VibeFeed />} />
                            <Route path="/assets/*" element={<AssetManagement />} />
                            <Route path="/flows/*" element={<FlowAutomation />} />
                            {/* Redirect audit-trail to payroll hub audit tab for backwards compatibility */}
                            <Route path="/audit-trail" element={<Navigate to="/admin/payrollrun?tab=audit" replace />} />

                        </Routes>
                    </main>
                </div>

                {/* Global Payslip Generation Progress - persists across tab navigation */}
                {showPayslipProgress && payslipJobId && (
                    <PayslipGenerationProgress
                        jobId={payslipJobId}
                        onComplete={handlePayslipJobComplete}
                        onCancel={handlePayslipJobCancel}
                        onClose={handlePayslipProgressClose}
                    />
                )}
            </div>

            {/* AI Avatar & FTUE */}
            <AIAvatarContainer />
        </PayslipJobContext.Provider>
    );
};

export default AdminPortal;
